import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../modules/api/client";
import { PromoAdminItem, PromoUpsertDto } from "./types";
import cls from "./PromoAdmin.module.scss";

const isoOrNull = (v: string) => v.trim() ? new Date(v).toISOString() : null;

const PromoForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isNew = id === "new";
    const nav = useNavigate();

    const [model, setModel] = useState<PromoUpsertDto>({
        cardImage: "",
        modalImage: "",
        weight: 10,
        startsAt: null,
        endsAt: null,
    });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        if (isNew) return;
        (async () => {
            try {
                setLoading(true);
                const data = await api.get<PromoAdminItem>(`/admin/promos/${id}`);
                setModel({
                    cardImage: data.cardImage,
                    modalImage: data.modalImage,
                    weight: data.weight,
                    startsAt: data.startsAt,
                    endsAt: data.endsAt
                });
            } catch (e: any) {
                setErr(e?.message || "Ошибка загрузки");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isNew]);

    const valid = useMemo(() => {
        return Boolean(model.cardImage && model.modalImage && typeof model.weight === "number");
    }, [model]);

    const onSave = async () => {
        if (!valid) { alert("Заполните обязательные поля"); return; }
        setSaving(true);
        setErr(null);
        try {
            const payload: PromoUpsertDto = {
                cardImage: model.cardImage,
                modalImage: model.modalImage,
                weight: Number(model.weight ?? 0),
                startsAt: model.startsAt,
                endsAt: model.endsAt,
            };
            if (isNew) {
                const created = await api.post<PromoAdminItem>("/admin/promos", payload);
                nav(`/admin/promos/${created.id}`);
            } else {
                await api.patch<PromoAdminItem>(`/admin/promos/${id}`, payload);
                alert("Сохранено");
            }
        } catch (e: any) {
            setErr(e?.message || "Не удалось сохранить");
        } finally {
            setSaving(false);
        }
    };

    const onUpload = async (field: "cardImage" | "modalImage", file: File | null) => {
        if (!file) return;
        try {
            const { url } = await api.upload<{ url: string }>("/api/uploads", file, "file");
            setModel(m => ({ ...m, [field]: url }));
        } catch (e: any) {
            alert(e?.message || "Загрузка не удалась");
        }
    };

    if (loading) return <div className={cls.card}>Загрузка…</div>;

    return (
        <div className={cls.card}>
            <div className={cls.header}>
                <h2 style={{ margin:0 }}>{isNew ? "Создать акцию" : `Акция #${id}`}</h2>
                <div className={cls.toolbar}>
                    <button className={cls.btn} onClick={onSave} disabled={!valid || saving}>
                        {saving ? "Сохранение…" : "Сохранить"}
                    </button>
                </div>
            </div>

            {err && <div style={{ color:"crimson", marginBottom:12 }}>{err}</div>}

            <div className={cls.row}>
                <div className={cls.col}>
                    <label>Карточка (cardImage)</label>
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                        <div className={cls.preview} style={{ backgroundImage:`url('${model.cardImage || ""}')` }} />
                        <input
                            className={cls.input}
                            placeholder="https://cdn.example.com/card.jpg"
                            value={model.cardImage || ""}
                            onChange={e => setModel(m => ({ ...m, cardImage: e.target.value }))}
                        />
                    </div>
                    <div style={{ marginTop:8 }}>
                        <input type="file" accept="image/*" onChange={e => onUpload("cardImage", e.target.files?.[0] || null)} />
                    </div>
                </div>

                <div className={cls.col}>
                    <label>Деталка (modalImage)</label>
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                        <div className={cls.preview} style={{ backgroundImage:`url('${model.modalImage || ""}')` }} />
                        <input
                            className={cls.input}
                            placeholder="https://cdn.example.com/modal.jpg"
                            value={model.modalImage || ""}
                            onChange={e => setModel(m => ({ ...m, modalImage: e.target.value }))}
                        />
                    </div>
                    <div style={{ marginTop:8 }}>
                        <input type="file" accept="image/*" onChange={e => onUpload("modalImage", e.target.files?.[0] || null)} />
                    </div>
                </div>
            </div>

            <div className={cls.row} style={{ marginTop:12 }}>
                <div className={cls.col}>
                    <label>Вес (чем меньше, тем выше)</label>
                    <input
                        type="number"
                        className={cls.input}
                        value={Number(model.weight ?? 0)}
                        onChange={e => setModel(m => ({ ...m, weight: Number(e.target.value) }))}
                    />
                </div>
                <div className={cls.col}>
                    <label>Период действия</label>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                        <input
                            className={cls.input}
                            type="datetime-local"
                            value={model.startsAt ? new Date(model.startsAt).toISOString().slice(0,16) : ""}
                            onChange={e => setModel(m => ({ ...m, startsAt: isoOrNull(e.target.value) }))}
                            placeholder="Начало"
                        />
                        <input
                            className={cls.input}
                            type="datetime-local"
                            value={model.endsAt ? new Date(model.endsAt).toISOString().slice(0,16) : ""}
                            onChange={e => setModel(m => ({ ...m, endsAt: isoOrNull(e.target.value) }))}
                            placeholder="Окончание"
                        />
                    </div>
                </div>
            </div>

            {!isNew && (
                <div style={{ marginTop:16 }}>
                    <button className={`${cls.btn} ${cls.ghost}`} onClick={() => window.open(`/api/promos`, "_blank")}>
                        Проверить JSON витрины
                    </button>
                </div>
            )}
        </div>
    );
};

export default PromoForm;
